import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { PrivateWorkspaceAgentAssetListPillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentAssetListPillChip';
import { usePrivateWorkspaceAgentPinnedArtifacts } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentPinnedArtifacts';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { type AssetListPill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { markWorkspaceAgentArtifactHydrationPill } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentArtifactHydration';
import { useWorkspaceAgentAssetListViewStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentAssetListViewStore';

type PinnedArtifactEntryProps = {
  artifactId: string;
  onClosePanel: () => void;
};

const PinnedArtifactEntry = memo(({ artifactId, onClosePanel }: PinnedArtifactEntryProps) => {
  const pill = useMemo((): AssetListPill => {
    const candidate: AssetListPill = {
      count: 1,
      id: artifactId,
      type: 'assetList',
    };
    markWorkspaceAgentArtifactHydrationPill(candidate);
    return candidate;
  }, [artifactId]);

  const handleShowInThread = useCallback(() => {
    onClosePanel();

    window.setTimeout(() => {
      document.querySelector(`[data-pinned-item="${CSS.escape(artifactId)}"]`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 0);
  }, [artifactId, onClosePanel]);

  return (
    <PrivateWorkspaceAgentAssetListPillChip
      isStreaming={false}
      onOpenArtifactCallback={onClosePanel}
      onShowInThread={handleShowInThread}
      pill={pill}
      showSaveActions={false}
    />
  );
});

PinnedArtifactEntry.displayName = 'PinnedArtifactEntry';

export const PrivateWorkspaceAgentPinnedItemsPanel = memo(() => {
  const { sessionId } = usePrivateWorkspaceAgentPanelSession();
  const { pinnedArtifacts } = usePrivateWorkspaceAgentPinnedArtifacts();
  const pinnedItemsOpen = useWorkspaceAgentAssetListViewStore((state) => state.pinnedItemsOpen);
  const setPinnedItemsOpen = useWorkspaceAgentAssetListViewStore((state) => state.setPinnedItemsOpen);

  const handleClose = useCallback(() => {
    setPinnedItemsOpen(false);
  }, [setPinnedItemsOpen]);

  useEffect(() => {
    if (pinnedArtifacts.length === 0 && pinnedItemsOpen) {
      setPinnedItemsOpen(false);
    }
  }, [pinnedArtifacts.length, pinnedItemsOpen, setPinnedItemsOpen]);

  const previousSessionIdRef = useRef(sessionId);

  useEffect(() => {
    if (previousSessionIdRef.current !== sessionId) {
      previousSessionIdRef.current = sessionId;
      setPinnedItemsOpen(false);
    }
  }, [sessionId, setPinnedItemsOpen]);

  useEffect(() => {
    if (!pinnedItemsOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleClose, pinnedItemsOpen]);

  if (!pinnedItemsOpen || pinnedArtifacts.length === 0) {
    return null;
  }

  return (
    <>
      <button
        aria-label="Close pinned items"
        className="absolute inset-0 z-20 cursor-default"
        data-testid="WORKSPACE_AGENT_PINNED_ITEMS_BACKDROP"
        onClick={handleClose}
        type="button"
      />
      <section
        aria-label="Pinned items"
        className="absolute inset-x-0 top-0 z-30 flex max-h-full flex-col overflow-hidden rounded-b-12 ads:border-default border-gray-4 border-b ads:bg-base bg-gray-1 shadow-s"
        data-testid="WORKSPACE_AGENT_PINNED_ITEMS_PANEL"
      >
        <p className="shrink-0 px-3 pt-3 pb-2 font-semibold ads:text-primary text-14 text-gray-12">Pinned items</p>
        <div className="flex min-h-0 flex-col gap-1.5 overflow-y-auto px-3 pb-3">
          {pinnedArtifacts.map((artifact) => (
            <PinnedArtifactEntry artifactId={artifact.id} key={artifact.id} onClosePanel={handleClose} />
          ))}
        </div>
      </section>
    </>
  );
});

PrivateWorkspaceAgentPinnedItemsPanel.displayName = 'PrivateWorkspaceAgentPinnedItemsPanel';
